/**
 * Parse a Companion "choices" block out of assistant prose (shared client util).
 *
 * When the model offers a short set of next steps it appends a fenced block:
 *
 *   ```tonnie-choices
 *   Show me the most important tasks first
 *   What should I do this week?
 *   ```
 *
 * The FE renders those as clickable chips (+ an "Other" affordance) and strips
 * the block from the rendered markdown — a structured, model-driven "choices
 * envelope" without a fragile mid-stream protocol event. While streaming, an
 * unclosed block simply renders as a code fence until it completes. Shared here
 * so journey-web AND business-web parse the block identically (no fork).
 */

const CHOICES_BLOCK = /```tonnie-choices\s*\n([\s\S]*?)\n```/i;
const MAX_CHOICES = 4;

export interface ParsedChoices {
  /** Prose with the choices block removed (trimmed). */
  text: string;
  /** Up to 4 short options; empty when no (complete) block is present. */
  options: string[];
}

export function parseChoices(raw: string): ParsedChoices {
  const match = raw.match(CHOICES_BLOCK);
  if (!match) return { text: raw, options: [] };

  const options = match[1]
    .split('\n')
    .map((line) => line.replace(/^\s*(?:[-*•]|\d+[.)])\s+/, '').trim())
    .filter((line) => line.length > 0)
    .slice(0, MAX_CHOICES);

  const text = raw.replace(CHOICES_BLOCK, '').trim();
  return { text, options };
}
